import { Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { BrandService } from '../brand/brand.service';
import { filter, take } from 'rxjs';

export type Feature = (typeof FeatureService)['FEATURE_LIST'][number];

@Injectable({ providedIn: 'root' })
export class FeatureService {
    private static FEATURE_LIST = ['design-api', 'conversion-to-design-api', 'design-api-put'] as const;
    private _activatedFeatures = new Set<Feature>();
    readonly features = [...FeatureService.FEATURE_LIST];
    private accountSlug: string;

    constructor(
        private router: Router,
        private brandService: BrandService
    ) {
        const url = new URL(window.location.href);
        const featureParams = url.searchParams.get('features');

        if (featureParams) {
            this.parseQueryParam(featureParams);
        }

        this.brandService.accountSlug$.pipe(takeUntilDestroyed()).subscribe(accountSlug => {
            this.accountSlug = accountSlug;
        });
    }

    private parseQueryParam(param: string): void {
        const values = param.split(',');
        for (const value of values) {
            for (const feature of FeatureService.FEATURE_LIST) {
                if (feature.toLocaleLowerCase() === value.toLowerCase()) {
                    this._activatedFeatures.add(feature);
                }
            }
        }
    }

    getActivatedFeatures(): Feature[] {
        return [...this._activatedFeatures];
    }

    isFeatureEnabled(feature: Feature): boolean {
        return this._activatedFeatures.has(feature);
    }

    setEnabledFeatures(features: Feature[]): void {
        this._activatedFeatures.clear();
        for (const feature of features) {
            this._activatedFeatures.add(feature);
        }

        const featuresParam = this._activatedFeatures.size
            ? [...this._activatedFeatures].join(',')
            : undefined;

        if (this.router.url === '/') {
            this.router.events
                .pipe(
                    filter(event => event instanceof NavigationEnd),
                    take(1)
                )
                .subscribe(() => {
                    this.changeFeaturesRoute(featuresParam);
                });
        } else {
            this.changeFeaturesRoute(featuresParam);
        }
    }

    isDesignApiFeatureEnabled(): boolean {
        return (
            this.isFeatureEnabled('design-api') ||
            this.isFeatureEnabled('conversion-to-design-api') ||
            this.isFeatureEnabled('design-api-put')
        );
    }

    private changeFeaturesRoute(featuresParam: string | undefined): void {
        this.router.navigate([], {
            queryParams: { features: featuresParam },
            queryParamsHandling: 'merge'
        });
    }
}
